import {defineMessages} from 'react-intl';

import {SlackOutlined, TeamOutlined, XOutlined} from '@ant-design/icons';

import facebookIcon from '@assets/facebook.png';
import servicenowIcon from '@assets/servicenow.png';
import slackIcon from '@assets/slack.png';
import teamsIcon from '@assets/teams.png';
import xIcon from '@assets/x-logo.png';

import {FEATURES} from '@constants/features';
import {
  MANAGE_FACEBOOK_CONFIGURATION,
  MANAGE_MS_TEAMS_CONFIGURATION,
  MANAGE_SERVICENOW_CONFIGURATION,
  MANAGE_SLACK_CONFIGURATION,
  MANAGE_X_CONFIGURATION,
  VIEW_FACEBOOK_CONFIGURATION,
  VIEW_MS_TEAMS_CONFIGURATION,
  VIEW_SERVICENOW_CONFIGURATION,
  VIEW_SLACK_CONFIGURATION,
  VIEW_X_CONFIGURATION,
} from '@constants/permissions';
import {
  FACEBOOK_INTEGRATION,
  MS_TEAMS_INTEGRATION,
  SERVICENOW_INTEGRATION,
  SLACK_INTEGRATION,
  X_INTEGRATION,
} from '@constants/subscriptions';

/**
 * MassNotification integration types
 */
export const SERVICENOW_INTEGRATION_TYPE = 'SERVICENOW';

export const SOCIAL_INTEGRATION_TYPE = {
  FACEBOOK: 'FACEBOOK',
  MS_TEAMS: 'MS_TEAMS',
  SLACK: 'SLACK',
  TWITTER_X: 'TWITTER_X',
};

export const SERVICENOW_INTEGRATION_SESSION_STORAGE_OAUTH_KEY = 'SERVICENOW_INTEGRATION_SESSION_STORAGE_OAUTH_KEY';

export const SOCIAL_INTEGRATION_OAUTH_KEY = 'SOCIAL_INTEGRATION_OAUTH_KEY';
export const SOCIAL_INTEGRATION_OAUTH_REDIRECT_SOURCE = {
  MY_ORGANIZATION: 'MY_ORGANIZATION',
  ORGANIZATION_PROFILE: 'ORGANIZATION_PROFILE',
};

/**
 * MassNotification integrations subscription map
 */
const SERVICENOW_INTEGRATION_SUBSCRIPTION_MAP = {
  [SERVICENOW_INTEGRATION_TYPE]: SERVICENOW_INTEGRATION,
};

export const SOCIAL_INTEGRATION_SUBSCRIPTION_MAP = {
  [SOCIAL_INTEGRATION_TYPE.SLACK]: SLACK_INTEGRATION,
  [SOCIAL_INTEGRATION_TYPE.MS_TEAMS]: MS_TEAMS_INTEGRATION,
  [SOCIAL_INTEGRATION_TYPE.FACEBOOK]: FACEBOOK_INTEGRATION,
  [SOCIAL_INTEGRATION_TYPE.TWITTER_X]: X_INTEGRATION,
};

export const MASS_NOTIFICATION_INTEGRATION_SUBSCRIPTION_MAP = {
  ...SERVICENOW_INTEGRATION_SUBSCRIPTION_MAP,
  ...SOCIAL_INTEGRATION_SUBSCRIPTION_MAP,
};

/**
 * MassNotification integrations feature toggle keys map
 */
export const SERVICENOW_INTEGRATION_FEATURE_MAP = {
  [SERVICENOW_INTEGRATION_TYPE]: FEATURES.MPM_9,
};

export const SOCIAL_INTEGRATION_FEATURE_MAP = {
  [SOCIAL_INTEGRATION_TYPE.FACEBOOK]: FEATURES.MPM_3,
  [SOCIAL_INTEGRATION_TYPE.TWITTER_X]: FEATURES.MPM_4,
};

/**
 * Social integration posting options configuration
 */
export const SOCIAL_INTEGRATION_POSTING_OPTION_CONFIG = {
  [SOCIAL_INTEGRATION_TYPE.SLACK]: {
    icon: SlackOutlined,
  },
  [SOCIAL_INTEGRATION_TYPE.TWITTER_X]: {
    icon: XOutlined,
    feature: SOCIAL_INTEGRATION_FEATURE_MAP[SOCIAL_INTEGRATION_TYPE.TWITTER_X],
  },
  [SOCIAL_INTEGRATION_TYPE.MS_TEAMS]: {
    icon: TeamOutlined,
  },
};

export const socialIntegrationMessages = defineMessages({
  [SOCIAL_INTEGRATION_TYPE.SLACK]: {
    id: 'ClientConfigurationIntegrations.constants.SLACK',
    defaultMessage: 'Slack',
  },
  [SOCIAL_INTEGRATION_TYPE.FACEBOOK]: {
    id: 'ClientConfigurationIntegrations.constants.FACEBOOK',
    defaultMessage: 'Facebook',
  },
  [SOCIAL_INTEGRATION_TYPE.MS_TEAMS]: {
    id: 'ClientConfigurationIntegrations.constants.MS_TEAMS',
    defaultMessage: 'Microsoft Teams',
  },
  [SOCIAL_INTEGRATION_TYPE.TWITTER_X]: {
    id: 'ClientConfigurationIntegrations.constants.TWITTER_X',
    defaultMessage: 'X',
  },
  [SERVICENOW_INTEGRATION_TYPE]: {
    id: 'ClientConfigurationIntegrations.constants.SERVICENOW',
    defaultMessage: 'ServiceNow',
  },
  connectFailure: {
    id: 'ClientConfigurationIntegrations.constants.connectFailure',
    defaultMessage: 'Unable to connect to {integrationType}',
  },
  connectSuccess: {
    id: 'ClientConfigurationIntegrations.constants.connectSuccess',
    defaultMessage: '{integrationType} account connected',
  },
  socialIntegration: {
    id: 'ClientConfigurationIntegrations.constants.socialIntegration',
    defaultMessage: 'Social Integration',
  },
  successEnabled: {
    id: 'ClientConfigurationIntegrations.constants.successEnabled',
    defaultMessage: '{integrationType} enabled',
  },
  successDisabled: {
    id: 'ClientConfigurationIntegrations.constants.successDisabled',
    defaultMessage: '{integrationType} disabled',
  },
  failureEnabled: {
    id: 'ClientConfigurationIntegrations.constants.failureEnabled',
    defaultMessage: 'Unable to enable {integrationType}',
  },
  failureDisabled: {
    id: 'ClientConfigurationIntegrations.constants.failureDisabled',
    defaultMessage: 'Unable to disable {integrationType}',
  },
  successRemove: {
    id: 'ClientConfigurationIntegrations.constants.successRemove',
    defaultMessage: '{integrationType} disconnected',
  },
  failureRemove: {
    id: 'ClientConfigurationIntegrations.constants.failureRemove',
    defaultMessage: 'Failed to disconnect {integrationType}',
  },
});

/**
 * MassNotification integrations configuration
 */
const SERVICENOW_INTEGRATION_CONFIG = [
  {
    feature: SERVICENOW_INTEGRATION_FEATURE_MAP[SERVICENOW_INTEGRATION_TYPE],
    permission: VIEW_SERVICENOW_CONFIGURATION,
    managePermission: MANAGE_SERVICENOW_CONFIGURATION,
    type: SERVICENOW_INTEGRATION_TYPE,
    subscription: SERVICENOW_INTEGRATION,
    logo: servicenowIcon,
  },
];

const SOCIAL_INTEGRATION_CONFIGS = [
  {
    permission: VIEW_SLACK_CONFIGURATION,
    managePermission: MANAGE_SLACK_CONFIGURATION,
    type: SOCIAL_INTEGRATION_TYPE.SLACK,
    subscription: SLACK_INTEGRATION,
    logo: slackIcon,
  },
  {
    permission: VIEW_MS_TEAMS_CONFIGURATION,
    managePermission: MANAGE_MS_TEAMS_CONFIGURATION,
    type: SOCIAL_INTEGRATION_TYPE.MS_TEAMS,
    subscription: MS_TEAMS_INTEGRATION,
    logo: teamsIcon,
  },
  {
    feature: SOCIAL_INTEGRATION_FEATURE_MAP[SOCIAL_INTEGRATION_TYPE.TWITTER_X],
    permission: VIEW_X_CONFIGURATION,
    managePermission: MANAGE_X_CONFIGURATION,
    type: SOCIAL_INTEGRATION_TYPE.TWITTER_X,
    subscription: X_INTEGRATION,
    logo: xIcon,
    allowMultipleAccounts: true,
  },
  {
    feature: SOCIAL_INTEGRATION_FEATURE_MAP[SOCIAL_INTEGRATION_TYPE.FACEBOOK],
    permission: VIEW_FACEBOOK_CONFIGURATION,
    managePermission: MANAGE_FACEBOOK_CONFIGURATION,
    type: SOCIAL_INTEGRATION_TYPE.FACEBOOK,
    subscription: FACEBOOK_INTEGRATION,
    logo: facebookIcon,
  },
];

export const MASS_NOTIFICATION_INTEGRATION_CONFIGS = [...SERVICENOW_INTEGRATION_CONFIG, ...SOCIAL_INTEGRATION_CONFIGS];

export const composeMessageSortOrder = [
  SOCIAL_INTEGRATION_TYPE.SLACK,
  SOCIAL_INTEGRATION_TYPE.TWITTER_X,
  SOCIAL_INTEGRATION_TYPE.MS_TEAMS,
  SOCIAL_INTEGRATION_TYPE.FACEBOOK,
];
